import React, { useState, useEffect } from 'react';
import {
    Table, Tag, Tooltip, Spin, Input, DatePicker, message
} from 'antd';
import {
    SearchOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import adminService from '../../services/adminService';

const { RangePicker } = DatePicker;

const ConsentRecordsPage = () => {
    const [records, setRecords] = useState([]);
    const [stats, setStats] = useState({ totalCount: 0, kvkkCount: 0, acikRizaCount: 0 });
    const [loading, setLoading] = useState(false);
    const [typeFilter, setTypeFilter] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);

    const fetchData = async () => {
        setLoading(true);
        try {
            const startDate = dateRange?.[0]?.format('YYYY-MM-DD') || null;
            const endDate = dateRange?.[1]?.format('YYYY-MM-DD') || null;
            const searchTerm = search.trim() || null;

            const [data, statsData] = await Promise.all([
                adminService.getConsentRecords(typeFilter, startDate, endDate, searchTerm, currentPage, pageSize),
                adminService.getConsentStats(),
            ]);
            setRecords(data.content);
            setTotalElements(data.totalElements);
            setStats(statsData);
        } catch (err) {
            message.error('Veriler yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [typeFilter, dateRange, currentPage, pageSize]);

    const handleSearch = () => {
        setCurrentPage(0);
        fetchData();
    };

    const columns = [
        {
            title: 'Onay Tipi',
            dataIndex: 'consentType',
            key: 'consentType',
            width: 120,
            render: (type) => (
                <Tag color={type === 'KVKK' ? 'blue' : 'purple'} style={{ fontWeight: 600 }}>
                    {type === 'KVKK' ? 'KVKK' : 'Açık Rıza'}
                </Tag>
            ),
        },
        {
            title: 'Müşteri',
            dataIndex: 'customerName',
            key: 'customerName',
            width: 160,
            render: (name) => (
                <span style={{ color: 'var(--admin-text-primary)', fontWeight: 500, fontSize: 13 }}>
                    {name || '-'}
                </span>
            ),
        },
        {
            title: 'Telefon',
            dataIndex: 'customerPhone',
            key: 'customerPhone',
            width: 140,
            render: (phone) => (
                <span style={{ color: 'var(--admin-text-secondary)', fontSize: 13 }}>
                    {phone || '-'}
                </span>
            ),
        },
        {
            title: 'IP Adresi',
            dataIndex: 'ipAddress',
            key: 'ipAddress',
            width: 130,
            render: (ip) => (
                <span style={{ color: 'var(--admin-text-tertiary)', fontSize: 12, fontFamily: 'monospace' }}>
                    {ip || '-'}
                </span>
            ),
        },
        {
            title: 'Metin Hash',
            dataIndex: 'textVersionHash',
            key: 'textVersionHash',
            width: 120,
            render: (hash) => hash ? (
                <Tooltip title={hash}>
                    <span style={{ color: 'var(--admin-text-tertiary)', fontSize: 11, fontFamily: 'monospace' }}>
                        {hash.substring(0, 12)}...
                    </span>
                </Tooltip>
            ) : '-',
        },
        {
            title: 'Tarih',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (date) => (
                date ? (
                    <span style={{ color: 'var(--admin-text-tertiary)', fontSize: 12 }}>
                        {new Date(date).toLocaleString('tr-TR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </span>
                ) : '-'
            ),
        },
        {
            title: 'Oturum ID',
            dataIndex: 'sessionId',
            key: 'sessionId',
            width: 120,
            render: (sid) => sid ? (
                <Tooltip title={sid}>
                    <span style={{ color: 'var(--admin-text-tertiary)', fontSize: 11, fontFamily: 'monospace' }}>
                        {sid.substring(0, 12)}...
                    </span>
                </Tooltip>
            ) : '-',
        },
    ];

    return (
        <div>
            {/* Page Header */}
            <div className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">Onay Kayıtları</h1>
                    <p className="admin-page-subtitle">
                        KVKK Aydınlatma Metni ve Açık Rıza Metni onay kayıtlarını görüntüleyin.
                    </p>
                </div>
            </div>

            {/* Stats Cards */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                <motion.div
                    whileHover={{ scale: 1.02 }}
                    style={{
                        flex: 1,
                        background: 'rgba(99, 102, 241, 0.1)',
                        border: '1px solid rgba(99, 102, 241, 0.2)',
                        borderRadius: 8,
                        padding: '16px 20px',
                    }}
                >
                    <div style={{ color: 'var(--admin-text-tertiary)', fontSize: 13 }}>Toplam</div>
                    <div style={{ color: '#6366f1', fontSize: 28, fontWeight: 700, marginTop: 4 }}>
                        {stats.totalCount || 0}
                    </div>
                </motion.div>
                <motion.div
                    whileHover={{ scale: 1.02 }}
                    style={{
                        flex: 1,
                        background: 'rgba(59, 130, 246, 0.1)',
                        border: '1px solid rgba(59, 130, 246, 0.2)',
                        borderRadius: 8,
                        padding: '16px 20px',
                    }}
                >
                    <div style={{ color: 'var(--admin-text-tertiary)', fontSize: 13 }}>KVKK</div>
                    <div style={{ color: '#3b82f6', fontSize: 28, fontWeight: 700, marginTop: 4 }}>
                        {stats.kvkkCount || 0}
                    </div>
                </motion.div>
                <motion.div
                    whileHover={{ scale: 1.02 }}
                    style={{
                        flex: 1,
                        background: 'rgba(168, 85, 247, 0.1)',
                        border: '1px solid rgba(168, 85, 247, 0.2)',
                        borderRadius: 8,
                        padding: '16px 20px',
                    }}
                >
                    <div style={{ color: 'var(--admin-text-tertiary)', fontSize: 13 }}>Açık Rıza</div>
                    <div style={{ color: '#a855f7', fontSize: 28, fontWeight: 700, marginTop: 4 }}>
                        {stats.acikRizaCount || 0}
                    </div>
                </motion.div>
            </div>

            {/* Filters */}
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--admin-text-tertiary)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: 4 }}>Filtre</span>
                {[
                    { key: null, label: 'Tümü', count: stats.totalCount },
                    { key: 'KVKK', label: 'KVKK', count: stats.kvkkCount },
                    { key: 'ACIK_RIZA', label: 'Açık Rıza', count: stats.acikRizaCount },
                ].map(({ key, label, count }) => {
                    const isActive = typeFilter === key;
                    return (
                        <button
                            key={label}
                            onClick={() => { setTypeFilter(isActive && key !== null ? null : key); setCurrentPage(0); }}
                            style={{
                                padding: '6px 14px',
                                borderRadius: 8,
                                border: `1px solid ${isActive ? 'rgba(99, 102, 241, 0.5)' : 'var(--admin-border-color)'}`,
                                background: isActive ? 'rgba(99, 102, 241, 0.15)' : 'var(--admin-glass-bg)',
                                color: isActive ? '#818cf8' : 'var(--admin-text-secondary)',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                fontFamily: 'inherit',
                            }}
                        >
                            {label} <span style={{ opacity: 0.7, marginLeft: 2 }}>{count || 0}</span>
                        </button>
                    );
                })}

                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    <RangePicker
                        size="small"
                        onChange={(dates) => { setDateRange(dates); setCurrentPage(0); }}
                        style={{
                            background: 'var(--admin-glass-bg)',
                            borderColor: 'var(--admin-border-color)',
                        }}
                        placeholder={['Başlangıç', 'Bitiş']}
                    />
                    <Input
                        size="small"
                        placeholder="Ara (isim, telefon, oturum)"
                        prefix={<SearchOutlined style={{ color: 'var(--admin-text-tertiary)' }} />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onPressEnter={handleSearch}
                        style={{
                            width: 220,
                            background: 'var(--admin-glass-bg)',
                            borderColor: 'var(--admin-border-color)',
                        }}
                    />
                </div>
            </div>

            {/* Table */}
            <motion.div
                className="admin-table-container"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                {loading ? (
                    <div className="admin-loading">
                        <Spin size="large" />
                    </div>
                ) : (
                    <Table
                        scroll={{ x: 'max-content' }}
                        columns={columns}
                        dataSource={records}
                        rowKey="id"
                        pagination={{
                            current: currentPage + 1,
                            pageSize: pageSize,
                            total: totalElements,
                            onChange: (page, size) => {
                                setCurrentPage(page - 1);
                                setPageSize(size);
                            },
                            showSizeChanger: true,
                            showTotal: (total) => `Toplam ${total} kayıt`,
                        }}
                    />
                )}
            </motion.div>
        </div>
    );
};

export default ConsentRecordsPage;
